import Service, { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type {
  Breakdown,
  CentralMappingFilterQueryParams,
  PmsValueType,
} from 'lumen/utils/central-mapping-constants';

const POLLING_INTERVAL_IN_MILLISECONDS = macroCondition(isTesting()) ? 0 : 1000;

export type SavingStatusRawResponse =
  | {
      in_progress: true;
      subscriptions_processed: number;
      total_subscriptions_processing: number;
    }
  | {
      in_progress: false;
      subscriptions_processed: null;
      total_subscriptions_processing: null;
    };

export type SavingStatusResponse =
  | {
      inProgress: true;
      subscriptionsProcessed: number;
      totalSubscriptionsProcessing: number;
    }
  | {
      inProgress: false;
      subscriptionsProcessed: null;
      totalSubscriptionsProcessing: null;
    };

export interface SavingStatus {
  subscriptionsProcessed: number;
  totalSubscriptionsProcessing: number;
}

export default class CentralMappingSavingService extends Service {
  @service declare ajax: AjaxService;
  @service declare centralMapping: Services['central-mapping'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  // because it's easier to handle the UI state this way.
  @tracked isProcessing = false;
  // Separate `isProcessing` property from the `savingStatus` even though they're in the same object in the API responses,
  @tracked savingStatus: SavingStatus | null = null;

  pollSavingStatusTask = task(
    { restartable: true },
    async ({
      breakdown,
      valueType,
      initialSavingStatus,
      pageSize,
      page,
      filterOptions = {},
      includeArchived,
      showSuccessToast = false,
    }: {
      breakdown: Breakdown;
      filterOptions?: CentralMappingFilterQueryParams;
      includeArchived?: boolean;
      initialSavingStatus: SavingStatus | null;
      page: number;
      pageSize: number;
      showSuccessToast?: boolean;
      valueType: PmsValueType;
    }): Promise<void> => {
      this.isProcessing = true;

      // The initial status is only passed to the first call of this task, after the GET/POST /mapping_entries response.
      // This is to avoid an empty status between the GET/POST response and first polling response.
      if (initialSavingStatus) {
        this.savingStatus = initialSavingStatus;
      }

      const queryParams: FormatUrlParams = {
        // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
        market_id: this.hotelAndMarketService.market?.id,
        breakdown,
        value_type: valueType,
      };
      const savingStatusUrl = formatUrl(
        '/apigateway/v1/app/mapping/mapping_entries/status',
        queryParams,
        this.ajax.host,
      );

      try {
        const savingStatusResponse = await this.ajax.request(savingStatusUrl, {
          method: 'GET',
          contentType: 'application/json',
        });

        // If processing is complete, fetch the (now updated) mapping entries again.
        // @ts-expect-error: 'savingStatusResponse' is of type 'unknown'.
        if (!savingStatusResponse.in_progress) {
          this.isProcessing = false;

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.centralMapping.getMappingEntriesTask.perform({
            breakdown,
            valueType,
            pageSize,
            page,
            filterOptions,
            includeArchived,
          });

          if (showSuccessToast) {
            this.toast.success(this.intl.t('general.changes_saved'), {
              showIcon: true,
            });
          }

          return;
        }

        this.savingStatus = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          totalSubscriptionsProcessing:
            // @ts-expect-error: 'savingStatusResponse' is of type 'unknown'.
            savingStatusResponse.total_subscriptions_processing,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          subscriptionsProcessed:
            // @ts-expect-error: 'savingStatusResponse' is of type 'unknown'.
            savingStatusResponse.subscriptions_processed,
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.schedulePollSavingStatusTask.perform({
          breakdown,
          valueType,
          pageSize,
          page,
          filterOptions,
          includeArchived,
          showSuccessToast,
        });
      } catch (error) {
        this.errorTracking.captureException(error);

        this.savingStatus = null;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.schedulePollSavingStatusTask.perform({
          breakdown,
          valueType,
          pageSize,
          page,
          filterOptions,
          includeArchived,
          showSuccessToast,
        });
      }
    },
  );

  schedulePollSavingStatusTask = task(
    { restartable: true },
    async ({
      breakdown,
      valueType,
      pageSize,
      page,
      filterOptions = {},
      includeArchived,
      showSuccessToast = false,
    }: {
      breakdown: Breakdown;
      filterOptions?: CentralMappingFilterQueryParams;
      includeArchived?: boolean;
      page: number;
      pageSize: number;
      showSuccessToast?: boolean;
      valueType: PmsValueType;
    }): Promise<void> => {
      await timeout(POLLING_INTERVAL_IN_MILLISECONDS);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.pollSavingStatusTask.perform({
        breakdown,
        valueType,
        initialSavingStatus: null,
        pageSize,
        page,
        filterOptions,
        includeArchived,
        showSuccessToast,
      });
    },
  );

  stopPollingSavingStatus(): void {
    this.isProcessing = false;

    void this.pollSavingStatusTask.cancelAll();
    void this.schedulePollSavingStatusTask.cancelAll();
  }
}

declare module '@ember/service' {
  interface Registry {
    'central-mapping/saving': CentralMappingSavingService;
  }
}
